import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router';
import { API_ROUTES, type Uuid } from '@zenda/api-contract';
import { getToken } from '../lib/api';
import { rotaDaConversa } from '../lib/rotas';
import { Icon } from './Icon';
import { useT } from '../lib/i18n';
import { useTip } from './Tip';
import '../styles/player.css';

export interface Faixa {
  readonly messageId: Uuid;
  /** Para onde o botão de voltar leva: a conversa de onde o áudio saiu. */
  readonly conversaId: Uuid;
  /** De quem é o áudio — o nome do paciente. */
  readonly titulo: string;
  /** Horário, para ela saber QUAL áudio está tocando numa conversa com vários. */
  readonly subtitulo: string;
}

interface Player {
  readonly atual: Faixa | null;
  readonly tocando: boolean;
  readonly carregando: boolean;
  readonly erro: string | null;
  readonly posicao: number;
  readonly duracao: number;
  readonly tocar: (faixa: Faixa) => void;
  readonly alternar: () => void;
  readonly fechar: () => void;
  readonly buscar: (segundos: number) => void;
}

const Contexto = createContext<Player | null>(null);

/**
 * Um áudio tocando de cada vez, fora da conversa.
 *
 * ── Por que global, e não um `<audio>` no balão ──
 * Um player dentro da mensagem prende a atendente ali: sair da conversa mata o
 * som. E é justamente enquanto ouve que ela precisa abrir a agenda, achar a
 * ficha do paciente ou responder outro. O elemento vive no shell, acima do
 * `<Outlet/>`, então trocar de página não o desmonta.
 *
 * ── Por que buscar com `fetch` e não apontar o `src` ──
 * Mesmo motivo da prévia de imagem: `<audio src>` não manda o header de
 * autenticação, e a alternativa seria token na URL.
 */
export function PlayerProvider({ children }: { readonly children: ReactNode }): ReactElement {
  const t = useT();
  const [atual, setAtual] = useState<Faixa | null>(null);
  const [tocando, setTocando] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [posicao, setPosicao] = useState(0);
  const [duracao, setDuracao] = useState(0);

  const audio = useRef<HTMLAudioElement | null>(null);
  const blobAtual = useRef<string | null>(null);

  // Um `Audio` só, criado uma vez. Um por faixa deixaria o anterior tocando
  // enquanto o novo começa — dois pacientes falando ao mesmo tempo.
  if (audio.current === null && typeof Audio !== 'undefined') {
    audio.current = new Audio();
  }

  const soltarBlob = useCallback(() => {
    if (blobAtual.current !== null) {
      URL.revokeObjectURL(blobAtual.current);
      blobAtual.current = null;
    }
  }, []);

  useEffect(() => {
    const el = audio.current;
    if (el === null) return;

    const aoTempo = (): void => setPosicao(el.currentTime);
    const aoDurar = (): void => setDuracao(Number.isFinite(el.duration) ? el.duration : 0);
    const aoTocar = (): void => setTocando(true);
    const aoPausar = (): void => setTocando(false);
    const aoAcabar = (): void => {
      setTocando(false);
      setPosicao(0);
    };

    el.addEventListener('timeupdate', aoTempo);
    el.addEventListener('loadedmetadata', aoDurar);
    el.addEventListener('durationchange', aoDurar);
    el.addEventListener('play', aoTocar);
    el.addEventListener('pause', aoPausar);
    el.addEventListener('ended', aoAcabar);

    return () => {
      el.removeEventListener('timeupdate', aoTempo);
      el.removeEventListener('loadedmetadata', aoDurar);
      el.removeEventListener('durationchange', aoDurar);
      el.removeEventListener('play', aoTocar);
      el.removeEventListener('pause', aoPausar);
      el.removeEventListener('ended', aoAcabar);
    };
  }, []);

  // Solta o blob quando a aba fecha: sem isto o arquivo fica na memória do
  // navegador até ele decidir limpar.
  useEffect(() => soltarBlob, [soltarBlob]);

  const tocar = useCallback(
    (faixa: Faixa) => {
      const el = audio.current;
      if (el === null) return;

      // Mesma faixa: é play/pause, não recarregar.
      if (atual?.messageId === faixa.messageId && blobAtual.current !== null) {
        if (el.paused) void el.play();
        else el.pause();
        return;
      }

      el.pause();
      soltarBlob();
      setAtual(faixa);
      setPosicao(0);
      setDuracao(0);
      setErro(null);
      setCarregando(true);

      void (async () => {
        try {
          const token = getToken();
          const r = await fetch(API_ROUTES.messageMedia(faixa.messageId), {
            headers: token === null ? {} : { Authorization: `Bearer ${token}` },
          });
          if (!r.ok) {
            const j: unknown = await r.json().catch(() => ({}));
            throw new Error((j as { message?: string }).message ?? t.ui.falhaNoAudio);
          }
          const url = URL.createObjectURL(await r.blob());
          blobAtual.current = url;
          el.src = url;
          await el.play();
        } catch (e) {
          setErro(e instanceof Error ? e.message : t.ui.falhaNoAudio);
        } finally {
          setCarregando(false);
        }
      })();
    },
    [atual, soltarBlob],
  );

  const alternar = useCallback(() => {
    const el = audio.current;
    if (el === null || blobAtual.current === null) return;
    if (el.paused) void el.play();
    else el.pause();
  }, []);

  const fechar = useCallback(() => {
    audio.current?.pause();
    soltarBlob();
    setAtual(null);
    setTocando(false);
    setPosicao(0);
    setDuracao(0);
    setErro(null);
  }, [soltarBlob]);

  const buscar = useCallback((segundos: number) => {
    const el = audio.current;
    if (el === null) return;
    el.currentTime = segundos;
    setPosicao(segundos);
  }, []);

  const valor = useMemo<Player>(
    () => ({ atual, tocando, carregando, erro, posicao, duracao, tocar, alternar, fechar, buscar }),
    [atual, tocando, carregando, erro, posicao, duracao, tocar, alternar, fechar, buscar],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function usePlayer(): Player {
  const p = useContext(Contexto);
  if (p === null) throw new Error('usePlayer fora de <PlayerProvider>');
  return p;
}

function mmss(s: number): string {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
}

/**
 * A barra na lateral, acima de quem está logado.
 *
 * Só existe com algo tocando: uma barra vazia permanente ocuparia espaço da
 * navegação para não dizer nada.
 */
export function PlayerBar(): ReactElement | null {
  const t = useT();
  const p = usePlayer();
  const navegar = useNavigate();
  const tip = useTip();
  if (p.atual === null) return null;

  const faixa = p.atual;
  const andado = p.duracao === 0 ? 0 : (p.posicao / p.duracao) * 100;

  return (
    <div className="player">
      <div className="player__topo">
        <button
          type="button"
          className="player__play"
          onClick={p.alternar}
          disabled={p.carregando || p.erro !== null}
          aria-label={p.tocando ? t.player.pausar : t.player.tocar}
          // Recolhida, o botão é a barra inteira: sem isto não dá para saber
          // qual áudio está tocando.
          {...tip(`${faixa.titulo} · ${faixa.subtitulo}`)}
        >
          <Icon name={p.carregando ? 'clock' : p.tocando ? 'pause' : 'play'} size={15} />
        </button>

        <div className="player__id">
          <p className="player__titulo">{p.atual.titulo}</p>
          <p className="player__sub">{p.erro ?? `${t.player.audio} · ${p.atual.subtitulo}`}</p>
        </div>

        <button
          type="button"
          className="player__ir"
          onClick={() => navegar(rotaDaConversa(faixa.conversaId, faixa.messageId))}
          aria-label={t.player.irParaConversa}
          {...tip(t.player.irParaConversa)}
        >
          <Icon name="chat" size={13} />
        </button>

        <button
          type="button"
          className="player__fechar"
          onClick={p.fechar}
          aria-label={t.player.fechar}
        >
          <Icon name="x" size={13} />
        </button>
      </div>

      {p.erro !== null ? null : (
        <div className="player__linha">
          {/* `range` e não uma barra desenhada: teclado, leitor de tela e
              arrastar vêm de graça, e um áudio de dois minutos precisa dos três
              para ser navegável. */}
          {/* O trecho ja ouvido pinta o trilho por gradiente: `input[range]` nao
              tem pseudo-elemento de progresso que funcione nos dois motores. */}
          <input
            type="range"
            className="player__range"
            style={{ '--andado': `${andado}%` } as CSSProperties}
            min={0}
            max={p.duracao || 0}
            step={0.1}
            value={p.posicao}
            onChange={(e) => p.buscar(Number(e.target.value))}
            aria-label={t.player.posicao}
          />
          <span className="player__tempo num">
            {mmss(p.posicao)} / {mmss(p.duracao)}
          </span>
        </div>
      )}
    </div>
  );
}
